import { useEffect, useMemo, useRef, useState, useCallback } from "react"
import type { RefObject } from "react"
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query"
import { Skeleton, Tabs, TabsContent, TabsList } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { opportunityCreativesInfiniteQueryOptions, opportunityKeys } from "@/lib/queries"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { VirtualizedCreativeGrid } from "./VirtualizedCreativeGrid"
import { trackOpportunityExamplesTabSelected } from "@/lib/analytics-events"

export interface OpportunityCreativesProps {
  opportunityId: string
  opportunitySetId?: string
  opportunityTitle?: string
  segment1?: string | null
  segment2?: string | null
  segment3?: string | null
  feature?: string | null
  subFeature?: string | null
  dataset?: string | null
  engine?: string | null
  industry?: string | null
  /** Drawer scroll container the creatives grid virtualizes against. */
  scrollContainerRef?: RefObject<HTMLElement | null>
}

export const PAGE_SIZE = 100
const INITIAL_SKELETON_CARDS = 8
const NEXT_PAGE_SKELETON_CARDS = 4
// Sheet slide-in animation duration (matches data-[state=open]:duration-300)
const SHEET_ANIMATION_MS = 350

function CreativeCardSkeleton() {
  return (
    <div className="relative overflow-hidden rounded-md border">
      <Skeleton className="aspect-[4/5] rounded-none" />
      <div className="px-2.5 py-2">
        <Skeleton className="h-3.5 w-2/3" />
      </div>
    </div>
  )
}

function CreativesSkeleton() {
  return (
    <div>
      <div className="mb-4 flex gap-5 border-b border-border-subtle pb-3">
        {[12, 20, 20].map((labelWidth, i) => (
          <div key={i} className="flex items-center gap-2">
            <Skeleton className="h-3.5" style={{ width: labelWidth * 4 }} />
            <Skeleton className="h-4 w-6 rounded-full" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {Array.from({ length: INITIAL_SKELETON_CARDS }).map((_, i) => (
          <CreativeCardSkeleton key={i} />
        ))}
      </div>
    </div>
  )
}

interface SegmentFiltersProps {
  segment1?: string | null
  segment2?: string | null
  segment3?: string | null
}

function SegmentFilters({ segment1, segment2, segment3 }: SegmentFiltersProps) {
  const { t } = useTranslation()

  const entries = useMemo(() => {
    const labelMap: [string, string | null | undefined, string][] = [
      ["segment1", segment1, t("opportunities.drawer.segmentTextOverlay")],
      ["segment2", segment2, t("opportunities.drawer.segmentVoiceOver")],
      ["segment3", segment3, t("opportunities.drawer.segmentVisual")],
    ]
    return labelMap.filter(([, val]) => typeof val === "string" && val.trim() !== "") as [string, string, string][]
  }, [segment1, segment2, segment3, t])

  if (entries.length === 0) return null

  return (
    <div className="mb-3 text-xs text-muted-foreground" data-testid="segment-filters">
      <span className="font-medium">{t("opportunities.drawer.segmentDisplayingWhere")}</span>
      {entries.map(([key, val, label], i) => (
        <span key={key}>
          {i > 0 && <span className="mx-1 font-medium">{t("opportunities.drawer.segmentOr")}</span>}
          <span>{" "}{label} {t("opportunities.drawer.segmentIncludes")} &ldquo;{val}&rdquo;</span>
        </span>
      ))}
    </div>
  )
}

export function OpportunityCreatives({ opportunityId, opportunitySetId, opportunityTitle, segment1, segment2, segment3, feature, subFeature, dataset, engine, industry, scrollContainerRef }: OpportunityCreativesProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const containerRef = useRef<HTMLDivElement>(null)
  const bottomSentinelRef = useRef<HTMLDivElement>(null)
  const activeTabRef = useRef('client')
  const tabEnteredAtRef = useRef(Date.now())
  const [enteredForId, setEnteredForId] = useState<string | null>(null)
  const hasEnteredView = enteredForId === opportunityId

  // Reset tab tracking when switching between opportunities
  useEffect(() => {
    activeTabRef.current = 'client'
    tabEnteredAtRef.current = Date.now()
  }, [opportunityId])

  // Drop this opportunity's cached creative pages when the drawer closes or the user
  // switches opportunities. Each set holds ~100 creatives per opportunity; without
  // eviction the inactive InfiniteData lingers for the full gcTime, so browsing many
  // opportunities across sets steadily inflates the heap. Re-opening refetches cheaply.
  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: opportunityKeys.creativesInfinite(opportunityId, PAGE_SIZE),
      })
    }
  }, [opportunityId, queryClient])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    let settled = false
    const settle = () => {
      if (settled) return
      settled = true
      setEnteredForId(opportunityId)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          settle()
          observer.disconnect()
        }
      },
      { rootMargin: "100px 0px" },
    )
    observer.observe(el)

    // The Sheet uses a compositor-driven CSS animation (slide-in-from-right,
    // 300ms). Some browsers don't re-fire IntersectionObserver after an
    // ancestor transform changes, so the initial `false` can get stuck.
    // After the animation completes, check directly whether the section is
    // within the viewport and enable the query if so.
    const fallback = setTimeout(() => {
      const rect = el.getBoundingClientRect()
      if (rect.top < window.innerHeight + 100 && rect.bottom > -100) {
        settle()
      }
    }, SHEET_ANIMATION_MS)

    return () => {
      observer.disconnect()
      clearTimeout(fallback)
    }
  }, [opportunityId])

  const {
    data,
    isPending,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...opportunityCreativesInfiniteQueryOptions(opportunityId, PAGE_SIZE),
    enabled: hasEnteredView,
  })

  const items = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  )
  const creativesStatus = data?.pages[0]?.creativesStatus ?? null
  const totals = data?.pages[0]?.totals

  const client = useMemo(() => items.filter((c) => c.source === "client"), [items])
  const competitor = useMemo(() => items.filter((c) => c.source === "competitor"), [items])
  const benchmark = useMemo(() => items.filter((c) => c.source === "benchmark"), [items])

  const clientBadge = totals?.clientCount ?? client.length
  const competitorBadge = totals?.competitorCount ?? competitor.length
  const benchmarkBadge = totals?.benchmarkCount ?? benchmark.length

  const handleTabChange = useCallback((newTab: string) => {
    const previousTab = activeTabRef.current
    const timeOnPrevious = Date.now() - tabEnteredAtRef.current

    const countMap: Record<string, number> = {
      client: clientBadge,
      competitor: competitorBadge,
      benchmark: benchmarkBadge,
    }

    trackOpportunityExamplesTabSelected({
      opportunity_id: opportunityId,
      opportunity_set_id: opportunitySetId ?? '',
      tab_selected: newTab,
      tab_previous: previousTab,
      creative_count_in_tab: countMap[newTab] ?? 0,
      time_on_previous_tab_ms: timeOnPrevious,
    })

    activeTabRef.current = newTab
    tabEnteredAtRef.current = Date.now()
  }, [opportunityId, opportunitySetId, clientBadge, competitorBadge, benchmarkBadge])

  // Drives infinite loading against the drawer scroll container. Because the
  // grid is virtualized, the bottom sentinel only enters view once the user has
  // scrolled the full (reserved) grid height — i.e. the real list bottom. Falls
  // back to the section's own container when rendered without a drawer scroller.
  useInfiniteScroll(
    bottomSentinelRef,
    scrollContainerRef ?? containerRef,
    fetchNextPage,
    !!hasNextPage,
    isFetchingNextPage,
    { recheckKey: items.length },
  )

  const sectionHeader = (
    <>
      <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t("opportunities.drawer.exampleCreatives")}
      </p>
      <SegmentFilters segment1={segment1} segment2={segment2} segment3={segment3} />
    </>
  )

  if (!hasEnteredView) {
    return <div ref={containerRef}>{sectionHeader}</div>
  }

  if (isPending) {
    return (
      <div ref={containerRef}>
        {sectionHeader}
        <CreativesSkeleton />
      </div>
    )
  }

  if (isError) {
    return (
      <div ref={containerRef}>
        {sectionHeader}
        <div className="flex flex-col items-center gap-2 py-6">
          <p className="text-sm text-muted-foreground">{t("opportunities.drawer.creativesError")}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-xs text-primary hover:underline"
          >
            {t("common.retry")}
          </button>
        </div>
      </div>
    )
  }

  if (creativesStatus === "LOADING") {
    return (
      <div ref={containerRef}>
        {sectionHeader}
        <div className="flex flex-col items-center gap-2 py-8">
          <div className="size-5 animate-spin rounded-full border-2 border-muted border-t-primary" aria-hidden="true" />
          <p className="text-xs text-muted-foreground">{t("opportunities.drawer.creativesGenerating")}</p>
        </div>
      </div>
    )
  }

  if (creativesStatus === "FAILED") {
    return (
      <div ref={containerRef}>
        {sectionHeader}
        <div className="flex flex-col items-center gap-2 py-6">
          <p className="text-sm text-muted-foreground">{t("opportunities.drawer.creativesGenerationFailed")}</p>
        </div>
      </div>
    )
  }

  const noCreativesLabel = t("opportunities.drawer.noCreatives")

  return (
    <div ref={containerRef}>
      {sectionHeader}
      <Tabs defaultValue="client" onValueChange={handleTabChange}>
        <TabsList
          variant="line"
          options={[
            { value: "client",     label: t("opportunities.drawer.client"),     badge: clientBadge },
            { value: "competitor", label: t("opportunities.drawer.competitor"), badge: competitorBadge },
            { value: "benchmark",  label: t("opportunities.drawer.benchmark"),  badge: benchmarkBadge },
          ]}
        />
        <TabsContent value="client" className="pt-4">
          <VirtualizedCreativeGrid items={client} emptyLabel={noCreativesLabel} scrollContainerRef={scrollContainerRef} opportunityId={opportunityId} opportunitySetId={opportunitySetId} opportunityTitle={opportunityTitle} segment1={segment1} segment2={segment2} segment3={segment3} feature={feature} subFeature={subFeature} dataset={dataset} engine={engine} industry={industry} />
        </TabsContent>
        <TabsContent value="competitor" className="pt-4">
          <VirtualizedCreativeGrid items={competitor} emptyLabel={noCreativesLabel} scrollContainerRef={scrollContainerRef} opportunityId={opportunityId} opportunitySetId={opportunitySetId} opportunityTitle={opportunityTitle} segment1={segment1} segment2={segment2} segment3={segment3} feature={feature} subFeature={subFeature} dataset={dataset} engine={engine} industry={industry} />
        </TabsContent>
        <TabsContent value="benchmark" className="pt-4">
          <VirtualizedCreativeGrid items={benchmark} emptyLabel={noCreativesLabel} scrollContainerRef={scrollContainerRef} opportunityId={opportunityId} opportunitySetId={opportunitySetId} opportunityTitle={opportunityTitle} segment1={segment1} segment2={segment2} segment3={segment3} feature={feature} subFeature={subFeature} dataset={dataset} engine={engine} industry={industry} />
        </TabsContent>
      </Tabs>
      {hasNextPage && (
        <div
          ref={bottomSentinelRef}
          className="mt-4"
          aria-live="polite"
          data-testid="creatives-page-sentinel"
        >
          {isFetchingNextPage ? (
            <div
              className="grid grid-cols-2 gap-4 sm:grid-cols-4"
              aria-label={t("opportunities.drawer.loadingMoreCreatives")}
            >
              {Array.from({ length: NEXT_PAGE_SKELETON_CARDS }).map((_, i) => (
                <CreativeCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div className="h-8" aria-hidden="true" />
          )}
        </div>
      )}
    </div>
  )
}
